import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import Button from '../../misc/Button';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import SettingsTabs from './SettingsTabs';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';
import InputSettingsField from './InputSettingsField';
import DecoratorSettings from './DecoratorSettings';
import RowSpecificDecoratorSettings from './RowSpecificDecoratorSettings';
import TableEditorRte from './TableEditorRte';

import { isSettingHidden, isStyleSettingHidden } from '../../../lib/settingsHelper';
import { hasAnyRowDecorators } from '../../../lib/decoratorHelper';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import FieldTypes from '../../../constants/FieldTypes';

import { updateSchemaWithReadableIds, updateFieldValueWithReadableIds, getSchemaRows } from '../../../lib/collectionHelper';
import { swapId } from '../../../lib/utils';
import { validateReadableId } from '@builttocreate/engine-utils';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledTitleWrapper = styled.div`
  margin: 0px 8px 12px 8px;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsCollectionSchema = ({
  fileId,
  sortedPages,
  fields,

  activeSchemaId,
  schema,
  activeFieldPosition,

  fieldId,
  fieldIdentifier,
  fieldPositionId,

  type,
  displayType,
  config,

  //Column Headers styles (from root fieldPosition)
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Cell styles (from root fieldPosition)
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  backgroundColor,
  padding,

  //Field/border styles (from root fieldPosition)
  borderColor,
  borderWidth,
  borderRadius,

  //Action handlers
  onChange,
  onPositionChange,
  onSchemaEdit,
  useReadableIds
}) => {


  /**
   * TBD: We are pulling this off the schema as we don't update schemaTable
   * You can update activeSchemaTable if you pass onSchemaEdit
   */

  const parentId = findParentKey(schema, activeSchemaId);
  const activeSchemaTable = { ...schema[activeSchemaId], parentId } || {};
  
  const targetField = fields.filter((f) => f._id === fieldId)[0];


  const { title, identifier, hidden, logic, required, rowDecorators } = activeSchemaTable;

  const activeSchemaRows = getSchemaRows(targetField?.value, schema, activeSchemaId);

  const countTables = Object.keys(schema).length;

  const isTitleHidden = isSettingHidden(config, 'title');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isIdHidden = isSettingHidden(config, '_id');
  const isRowDecoratorsHidden = isSettingHidden(config, 'rowDecorators');
  const isRowSpecificDecoratorsHidden = isSettingHidden(config, 'rowSpecificDecorators');
  const isStylesHidden = isSettingHidden(config, 'styles');

  //Title Styles
  const isTitleStyleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleStyleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleStyleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleStyleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleStyleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleStyleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleStyleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Column Styles
  const isColumnTitleFontSizeHidden = isStyleSettingHidden(config, 'columnTitleFontSize');
  const isColumnTitleFontColorHidden = isStyleSettingHidden(config, 'columnTitleFontColor');
  const isColumnTitleTextAlignHidden = isStyleSettingHidden(config, 'columnTitleTextAlign');
  const isColumnTitleFontStyleHidden = isStyleSettingHidden(config, 'columnTitleFontStyle');
  const isColumnTitleFontWeightHidden = isStyleSettingHidden(config, 'columnTitleFontWeight');
  const isColumnTitleTextTransformHidden = isStyleSettingHidden(config, 'columnTitleTextTransform');
  const isColumnTitleTextDecorationHidden = isStyleSettingHidden(config, 'columnTitleTextDecoration');
  const isColumnTitleBackgroundColorHidden = isStyleSettingHidden(config, 'columnTitleBackgroundColor');
  const isColumnTitlePaddingHidden = isStyleSettingHidden(config, 'columnTitlePadding');

  //Cell Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');

  const isCellSettingsHidden = isFontSizeHidden
    && isFontColorHidden
    && isTextAlignHidden
    && isFontStyleHidden
    && isFontWeightHidden
    && isTextTransformHidden
    && isTextDecorationHidden
    && isTextOverflowHidden
    && isBackgroundColorHidden
    && isPaddingHidden;

  const isColumnSettingsHidden = isColumnTitleFontSizeHidden
    && isColumnTitleFontColorHidden
    && isColumnTitleTextAlignHidden
    && isColumnTitleFontStyleHidden
    && isColumnTitleFontWeightHidden
    && isColumnTitleTextTransformHidden
    && isColumnTitleBackgroundColorHidden
    && isColumnTitlePaddingHidden
    && isColumnTitleTextDecorationHidden;

  const isTitleSettingsHidden = isTitleStyleFontSizeHidden
    && isTitleStyleFontColorHidden
    && isTitleStyleTextAlignHidden
    && isTitleStyleFontStyleHidden
    && isTitleStyleFontWeightHidden
    && isTitleStyleTextTransformHidden
    && isTitleStyleTextDecorationHidden;

  const isFieldSettingsHidden = isBorderColorHidden
    && isBorderRadiusHidden
    && isBorderWidthHidden;

  /**
   * Get per-schema styles from fieldPositionSchema.styles, falling back to root fieldPosition styles
   */
  const schemaStyles = activeFieldPosition?.fieldPositionSchema?.[activeSchemaId]?.styles || {};

  const theme = useContext(ThemeContext);

  // Title styles - per-schema override, else theme default title styles.
  // Nested schema titles are independent of the root field title and never
  // inherit legacy column styles; this mirrors the fallback used in the renderer.
  const displayTitleFontSize = schemaStyles.titleFontSize ?? theme?.field?.titleFontSize;
  const displayTitleFontColor = schemaStyles.titleFontColor ?? theme?.field?.titleFontColor;
  const displayTitleFontStyle = schemaStyles.titleFontStyle ?? theme?.field?.titleFontStyle;
  const displayTitleFontWeight = schemaStyles.titleFontWeight ?? theme?.field?.titleFontWeight;
  const displayTitleTextAlign = schemaStyles.titleTextAlign ?? theme?.field?.titleTextAlign;
  const displayTitleTextTransform = schemaStyles.titleTextTransform ?? theme?.field?.titleTextTransform;
  const displayTitleTextDecoration = schemaStyles.titleTextDecoration ?? theme?.field?.titleTextDecoration;

  // Column title styles - per-schema value or root fallback
  const displayColumnTitleFontSize = schemaStyles.columnTitleFontSize ?? columnTitleFontSize;
  const displayColumnTitleFontColor = schemaStyles.columnTitleFontColor ?? columnTitleFontColor;
  const displayColumnTitleFontStyle = schemaStyles.columnTitleFontStyle ?? columnTitleFontStyle;
  const displayColumnTitleFontWeight = schemaStyles.columnTitleFontWeight ?? columnTitleFontWeight;
  const displayColumnTitleTextAlign = schemaStyles.columnTitleTextAlign ?? columnTitleTextAlign;
  const displayColumnTitleTextTransform = schemaStyles.columnTitleTextTransform ?? columnTitleTextTransform;
  const displayColumnTitleTextDecoration = schemaStyles.columnTitleTextDecoration ?? columnTitleTextDecoration;
  const displayColumnTitleBackgroundColor = schemaStyles.columnTitleBackgroundColor ?? columnTitleBackgroundColor;
  const displayColumnTitlePadding = schemaStyles.columnTitlePadding ?? columnTitlePadding;

  // Cell styles - per-schema value or root fallback
  const displayFontSize = schemaStyles.fontSize ?? fontSize;
  const displayFontColor = schemaStyles.fontColor ?? fontColor;
  const displayFontStyle = schemaStyles.fontStyle ?? fontStyle;
  const displayFontWeight = schemaStyles.fontWeight ?? fontWeight;
  const displayTextAlign = schemaStyles.textAlign ?? textAlign;
  const displayTextTransform = schemaStyles.textTransform ?? textTransform;
  const displayTextDecoration = schemaStyles.textDecoration ?? textDecoration;
  const displayTextOverflow = schemaStyles.textOverflow ?? textOverflow;
  const displayBackgroundColor = schemaStyles.backgroundColor ?? backgroundColor;
  const displayPadding = schemaStyles.padding ?? padding;

  // Field/border styles - per-schema value or root fallback
  const displayBorderColor = schemaStyles.borderColor ?? borderColor;
  const displayBorderWidth = schemaStyles.borderWidth ?? borderWidth;
  const displayBorderRadius = schemaStyles.borderRadius ?? borderRadius;

  const [readableIdError, setReadableIdError] = useState('');

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== activeSchemaId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = activeSchemaId;

  }, [activeSchemaId, setReadableIdError]);

  const nextFieldSettingsIcon = displayType === FieldDisplayTypes.inputGroup
    ? ThemeIcons.fields.tableInputGroup
    : displayType === FieldDisplayTypes.rte
      ? ThemeIcons.fields.richText
      : ThemeIcons.fields.table;

  /**
   * Handle schema _id update
   * 
   * * Ensure the field schema has the newly updated schema _id
   * * Ensure the field new schema _id is updated in field.value for all rows and child rows
   * * Ensure the fieldPosition is updated to the new schema _id to not break things like column hidden, format, etc.
   */
  const handleSchemaIdUpdate = (nextSchemaId) => {

    if (!schema || !activeSchemaTable) return schema;

    const changes = {};

    // Deep clone schema to avoid mutation
    let nextSchema = JSON.parse(JSON.stringify(schema));

    /**
     * 1.1 Update the schema with the new readable schema ID
     */
    nextSchema = updateSchemaWithReadableIds(nextSchema, { [activeSchemaId]: nextSchemaId });
    changes.schema = nextSchema;

    /**
     * 1.2 Update the field value with the new readable schema ID
     */
    if (targetField && targetField.value){ 
      /**
       * Important Note: We are using the schema as it has the previous schema readable ID.
       * This is because the field value has not been updated with the new readable ID yet.
       */
      const flattenedArrayResult = flattenArray(schema, targetField.value);
      const nextRows = updateFieldValueWithReadableIds(targetField.value, flattenedArrayResult, { [activeSchemaId]: nextSchemaId });
      changes.value = nextRows;
    }

    /**
     * 1.3 Update the fieldPosition with the new readable schema ID
     */
    const nextFieldPositionSchema = swapId(activeFieldPosition?.fieldPositionSchema || {}, activeSchemaId, nextSchemaId);
    changes.fieldPositionSchema = nextFieldPositionSchema;

    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

    /**
     * 1.4 onSchemaEdit callback: Sets the new schema _id as the active schema id in the File. This ensures
     * the proper field settings, rows editor, etc. is active and using the new schema _id.
     */
    onSchemaEdit(nextSchemaId)

  };

  const handleSchemaTableUpdate = (value, type) => {

    if (!schema || !activeSchemaTable) return schema;

    // Deep clone schema to avoid mutation
    let nextSchema = JSON.parse(JSON.stringify(schema));
    nextSchema[activeSchemaId][type] = value;

    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextSchema });

  };

  const handleRowSpecificDecoratorCreate = (rowId, data) => {
    const nextValue = JSON.parse(JSON.stringify(targetField.value));
    const rowCommon = rowDecorators || [];
    updateRowDecorators(nextValue, schema, activeSchemaId, rowId, (currentAll, isInitialized) => {
      if (!isInitialized) {
        const merged = [...rowCommon];
        const existingActions = new Set(rowCommon.map((d) => d.action));
        if (!existingActions.has(data.action)) merged.push(data);
        return merged;
      }
      return [...currentAll, data];
    });
    const hasDecorators = hasAnyRowDecorators(rowCommon, getSchemaRows(nextValue, schema, activeSchemaId));
    const nextSchema = JSON.parse(JSON.stringify(schema));
    nextSchema[activeSchemaId].decorate = hasDecorators;
    onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextValue, schema: nextSchema });
  };

  const handleRowSpecificDecoratorChange = (rowId, decoratorAction, data) => {
    const nextValue = JSON.parse(JSON.stringify(targetField.value));
    const rowCommon = rowDecorators || [];
    updateRowDecorators(nextValue, schema, activeSchemaId, rowId, (currentAll, isInitialized) => {
      const all = isInitialized ? currentAll : rowCommon.map((d) => ({ ...d }));
      return all.map((d) => d.action === decoratorAction ? { ...d, ...data } : d);
    });
    onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextValue });
  };

  const handleRowSpecificDecoratorDelete = (rowId, decoratorAction) => {
    const nextValue = JSON.parse(JSON.stringify(targetField.value));
    const rowCommon = rowDecorators || [];
    updateRowDecorators(nextValue, schema, activeSchemaId, rowId, (currentAll, isInitialized) => {
      const all = isInitialized ? currentAll : rowCommon.map((d) => ({ ...d }));
      return all.filter((d) => d.action !== decoratorAction);
    });
    const hasDecorators = hasAnyRowDecorators(rowCommon, getSchemaRows(nextValue, schema, activeSchemaId));
    const nextSchema = JSON.parse(JSON.stringify(schema));
    nextSchema[activeSchemaId].decorate = hasDecorators;
    onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextValue, schema: nextSchema });
  };

  const handleDeleteSchemaTable = () => {
    if (!schema || !schema[activeSchemaId]) return;

    onSchemaEdit(null); // Clear the active schema table

    // Clone schema to avoid mutating the original state
    const updatedSchema = { ...schema };

    // Function to recursively delete all child tables
    const deleteSchemaRecursively = (id) => {
      if (!updatedSchema[id]) return;

      // Delete all child tables first
      const children = updatedSchema[id]?.children || [];
      children.forEach((childId) => deleteSchemaRecursively(childId));

      // Remove the current schema table
      delete updatedSchema[id];
    };

    // Call recursive function on the main schemaId
    deleteSchemaRecursively(activeSchemaId);

    // Remove references from any parent's `children` array
    Object.keys(updatedSchema).forEach((parentId) => {
      if (updatedSchema[parentId]?.children) {
        updatedSchema[parentId].children = updatedSchema[parentId].children.filter((childId) => childId !== activeSchemaId);
      }
    });

    onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema });
  };

  /**
   * Handle per-schema style updates
   * Stores style properties in fieldPositionSchema[activeSchemaId].styles
   */
  const handleSchemaStyleUpdate = (styleChanges) => {
    const data = activeFieldPosition?.fieldPositionSchema || {};
    const nextFieldPositionSchema = {
      ...data,
      [activeSchemaId]: {
        ...(data[activeSchemaId] || {}),
        styles: {
          ...(data[activeSchemaId]?.styles || {}),
          ...styleChanges
        }
      }
    };
    onPositionChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextFieldPositionSchema });
  };

  const updateSchemaSettings = useCallback((nextSettings) => {

    const data = activeFieldPosition?.fieldPositionSchema || {};

    const nextFieldPositionSchema = {
      ...data,
      [activeSchemaId]: {
        ...(data[activeSchemaId] || {}),
        settings: nextSettings,
      },
    };

    onPositionChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextFieldPositionSchema });

  }, [activeFieldPosition, activeSchemaId, fieldId, fieldIdentifier, fieldPositionId, onPositionChange]);

  const generateRteSettingId = () => {
    const timestamp = ((new Date().getTime() / 1000) | 0).toString(16);
    return timestamp + 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16)).toLowerCase();
  };

  const rteSchemaSettings = activeFieldPosition?.fieldPositionSchema?.[activeSchemaId]?.settings || [];

  const handleRteSettingsCreate = useCallback(() => {

    const nextSettings = [...rteSchemaSettings, { _id: generateRteSettingId(), title: '', value: '' }];

    updateSchemaSettings(nextSettings);

  }, [rteSchemaSettings, updateSchemaSettings]);

  const handleRteSettingsUpdate = useCallback((id, settingId, patch) => {

    const nextSettings = rteSchemaSettings.map((s) => s._id === settingId ? { ...s, ...patch } : s);

    updateSchemaSettings(nextSettings);

  }, [rteSchemaSettings, updateSchemaSettings]);

  const handleRteSettingsMoveLeft = useCallback((id, settingId) => {

    const nextSettings = [...rteSchemaSettings];

    const index = nextSettings.findIndex((s) => s._id === settingId);

    if (index <= 0) return;

    const temp = nextSettings[index];

    nextSettings[index] = nextSettings[index - 1];

    nextSettings[index - 1] = temp;

    updateSchemaSettings(nextSettings);

  }, [rteSchemaSettings, updateSchemaSettings]);

  const handleRteSettingsMoveRight = useCallback((id, settingId) => {

    const nextSettings = [...rteSchemaSettings];

    const index = nextSettings.findIndex((s) => s._id === settingId);

    if (index === -1 || index >= nextSettings.length - 1) return;

    const temp = nextSettings[index];

    nextSettings[index] = nextSettings[index + 1];

    nextSettings[index + 1] = temp;

    updateSchemaSettings(nextSettings);

  }, [rteSchemaSettings, updateSchemaSettings]);

  const handleRteSettingsDelete = useCallback((id, settingId) => {

    const nextSettings = rteSchemaSettings.filter((s) => s._id !== settingId);

    updateSchemaSettings(nextSettings);

  }, [rteSchemaSettings, updateSchemaSettings]);

  const rteActiveFieldPosition = useMemo(
    () => ({ ...activeFieldPosition, settings: rteSchemaSettings }),
    [activeFieldPosition, rteSchemaSettings]
  );

  /**
   * Styles node - only shown for non-root schemas
   */
  const stylesNode = activeSchemaTable.root || isStylesHidden ? null : (
    <>
      {isTitleSettingsHidden ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Title"
            height={270}
          >
            <FieldStyleSettings
              id={`${fieldId}-schema-title`}
              {...{
                fontSize: displayTitleFontSize,
                onFontSizeChange: isTitleStyleFontSizeHidden ? null : (value) => handleSchemaStyleUpdate({ titleFontSize: value }),

                fontColor: displayTitleFontColor,
                onFontColorChange: isTitleStyleFontColorHidden ? null : (value) => handleSchemaStyleUpdate({ titleFontColor: value }),

                textAlign: displayTitleTextAlign,
                onTextAlignChange: isTitleStyleTextAlignHidden ? null : (value) => handleSchemaStyleUpdate({ titleTextAlign: value }),

                fontStyle: displayTitleFontStyle,
                onFontStyleChange: isTitleStyleFontStyleHidden ? null : (value) => handleSchemaStyleUpdate({ titleFontStyle: value }),

                fontWeight: displayTitleFontWeight,
                onFontWeightChange: isTitleStyleFontWeightHidden ? null : (value) => handleSchemaStyleUpdate({ titleFontWeight: value }),

                textTransform: displayTitleTextTransform,
                onTextTransformChange: isTitleStyleTextTransformHidden ? null : (value) => handleSchemaStyleUpdate({ titleTextTransform: value }),

                textDecoration: displayTitleTextDecoration,
                onTextDecorationChange: isTitleStyleTextDecorationHidden ? null : (value) => handleSchemaStyleUpdate({ titleTextDecoration: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isColumnSettingsHidden ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Column"
            height={270}
          >
            <FieldStyleSettings
              id={`${fieldId}-schema-column`}
              {...{
                fontSize: displayColumnTitleFontSize,
                onFontSizeChange: isColumnTitleFontSizeHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleFontSize: value }),

                fontColor: displayColumnTitleFontColor,
                onFontColorChange: isColumnTitleFontColorHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleFontColor: value }),

                textAlign: displayColumnTitleTextAlign,
                onTextAlignChange: isColumnTitleTextAlignHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleTextAlign: value }),

                fontStyle: displayColumnTitleFontStyle,
                onFontStyleChange: isColumnTitleFontStyleHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleFontStyle: value }),

                fontWeight: displayColumnTitleFontWeight,
                onFontWeightChange: isColumnTitleFontWeightHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleFontWeight: value }),

                textTransform: displayColumnTitleTextTransform,
                onTextTransformChange: isColumnTitleTextTransformHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleTextTransform: value }),

                textDecoration: displayColumnTitleTextDecoration,
                onTextDecorationChange: isColumnTitleTextDecorationHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleTextDecoration: value }),

                backgroundColor: displayColumnTitleBackgroundColor,
                onBackgroundColorChange: isColumnTitleBackgroundColorHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitleBackgroundColor: value }),

                padding: displayColumnTitlePadding,
                onPaddingChange: isColumnTitlePaddingHidden ? null : (value) => handleSchemaStyleUpdate({ columnTitlePadding: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isCellSettingsHidden ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Cell"
            height={270}
          >
            <FieldStyleSettings
              id={`${fieldId}-schema-cell`}
              {...{
                fontSize: displayFontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => handleSchemaStyleUpdate({ fontSize: value }),

                fontColor: displayFontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => handleSchemaStyleUpdate({ fontColor: value }),

                textAlign: displayTextAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => handleSchemaStyleUpdate({ textAlign: value }),

                fontStyle: displayFontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => handleSchemaStyleUpdate({ fontStyle: value }),

                fontWeight: displayFontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => handleSchemaStyleUpdate({ fontWeight: value }),

                textTransform: displayTextTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => handleSchemaStyleUpdate({ textTransform: value }),

                textDecoration: displayTextDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => handleSchemaStyleUpdate({ textDecoration: value }),

                textOverflow: displayTextOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => handleSchemaStyleUpdate({ textOverflow: value }),

                backgroundColor: displayBackgroundColor,
                onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => handleSchemaStyleUpdate({ backgroundColor: value }),

                padding: displayPadding,
                onPaddingChange: isPaddingHidden ? null : (value) => handleSchemaStyleUpdate({ padding: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {isFieldSettingsHidden ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={150}
        >
          <FieldStyleSettings
            id={`${fieldId}-schema-field`}
            {...{
              borderColor: displayBorderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => handleSchemaStyleUpdate({ borderColor: value }),

              borderRadius: displayBorderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => handleSchemaStyleUpdate({ borderRadius: value }),

              borderWidth: displayBorderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => handleSchemaStyleUpdate({ borderWidth: value }),
            }}
          />
        </AccordionSettings>
      )}
    </>
  );

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledTitleWrapper>
            <InputSettingsField
              value={title}
              type="textarea"
              onChange={(i, value) => handleSchemaTableUpdate(value, 'title')}
            />
          </StyledTitleWrapper>

        </>
      )}
      {isRequiredHidden || activeSchemaTable.root == true ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => {
                handleSchemaTableUpdate(selected, 'required');
              }}
            />
          </StyledFieldWrapper>
          <DividerLine style={{ marginBottom: 0 }} />
        </>
      )}
      {isLogicHidden || countTables.length < 2 || activeSchemaTable.root ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <ConditionalLogicSettings
              fileId={fileId}
              targetLabel=" schema table"
              targetFieldId={fieldId}
              sortedPages={sortedPages}
              fields={fields}
              logic={logic}
              hasSchema
              schema={schema}
              activeSchemaTable={activeSchemaTable}
              onSubmit={(nextLogic) => {

                if (!nextLogic) return;

                const updatedSchema = { ...schema };
                const schemaId = activeSchemaTable.id;

                if (!updatedSchema[activeSchemaId]) return;

                updatedSchema[activeSchemaId] = {
                  ...updatedSchema[activeSchemaId],
                  logic: nextLogic
                };

                onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: updatedSchema });

              }}
            />
          </StyledFieldWrapper>
        </>
      )}
      {isHiddenHidden || activeSchemaTable.root ? null : (
        <>
          <DividerLine />
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.hide}
              iconColor={hidden ? theme.warningColor : theme.borderColor}
              id="hidden"
              label="Hidden"
              selected={hidden}
              onChange={(value) => handleSchemaTableUpdate(value, 'hidden')}
            />
          </StyledFieldWrapper>
        </>
      )}
        {isIdHidden || !useReadableIds ? null : (
        <>
          <StyledFieldWrapper>
            <InputSettingsField
              label="ID"
              fieldId={fieldId}
              value={activeSchemaId}
              rows={3}
              error={readableIdError ? true : false}
              disabled={!useReadableIds}
              onChange={(fieldId, value) => {

                //We don't want to check for duplicates for the root schema table as there will be only one root per collection field
                if (value &&!activeSchemaTable.root && !validateReadableId(value, fields, null, fieldId)) {
                  setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);
                } else {
                  setReadableIdError('');
                  if (value) {
                    handleSchemaIdUpdate(value)
                  }
                }

              }}
            />
            {readableIdError && (
              <StyledErrorMessage theme={theme}>
                {readableIdError}
              </StyledErrorMessage>
            )}
          </StyledFieldWrapper>

        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(i, value) => handleSchemaTableUpdate(value, 'identifier')}
            />
          </AccordionSettings>
        </>
      )}
      {isRowDecoratorsHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <SettingsLabel>Common Row Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <DecoratorSettings
            decorators={rowDecorators}
            onCreate={(data) => {
              // Only update the common schema rowDecorators array.
              // Do NOT push into rows' decorators.all — once a row has
              // decorators.all initialized, it's an independent set.
              // Rows without decorators.all will see this new decorator
              // automatically via mergeRowDecorators fallback to common.
              const current = rowDecorators || [];
              const nextDecorators = [...current, data];
              const nextSchema = JSON.parse(JSON.stringify(schema));
              nextSchema[activeSchemaId].rowDecorators = nextDecorators;
              nextSchema[activeSchemaId].decorate = true;
              onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextSchema });
            }}
            onChange={(decoratorAction, data) => {
              // Only update the common schema rowDecorators array.
              // Do NOT cascade changes to rows' decorators.all — those
              // are independent specific decorators. Rows without
              // decorators.all see common changes via mergeRowDecorators.
              const current = rowDecorators || [];
              const updated = current.map((d) => d.action === decoratorAction ? data : d);
              const nextSchema = JSON.parse(JSON.stringify(schema));
              nextSchema[activeSchemaId].rowDecorators = updated;
              onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextSchema });
            }}
            onDelete={(decoratorAction) => {
              // Only remove from the common schema rowDecorators array.
              // Do NOT filter rows' decorators.all — those entries are
              // independent specific decorators that should survive.
              // Compute hasDecorators from original rows (unchanged)
              // so that rows with specific decorators keep decorate=true.
              const current = rowDecorators || [];
              const updated = current.filter((d) => d.action !== decoratorAction);
              const schemaRows = getSchemaRows(targetField.value, schema, activeSchemaId);
              const hasDecorators = hasAnyRowDecorators(updated, schemaRows);
              const nextSchema = JSON.parse(JSON.stringify(schema));
              nextSchema[activeSchemaId].rowDecorators = updated;
              nextSchema[activeSchemaId].decorate = hasDecorators;
              onChange(fieldId, fieldIdentifier, fieldPositionId, { schema: nextSchema });
            }}
          />
        </>
      )}
      {isRowSpecificDecoratorsHidden ? null : (
        <>
          <SettingsLabel>Specific Row Decorators</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <RowSpecificDecoratorSettings
            rows={activeSchemaRows}
            rowCommonDecorators={rowDecorators}
            onCreate={(rowId, data) => {
              handleRowSpecificDecoratorCreate(rowId, data);
            }}
            onChange={(rowId, decoratorAction, data) => {
              handleRowSpecificDecoratorChange(rowId, decoratorAction, data);
            }}
            onDelete={(rowId, decoratorAction) => {
              handleRowSpecificDecoratorDelete(rowId, decoratorAction);
            }}
          />
        </>
      )}
      {displayType === FieldDisplayTypes.rte ? (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            isOpen
            title="RTE Columns"
            height={240}
            enforceHeight
          >
            <TableEditorRte
              id={fieldId}
              fieldId={fieldId}
              activeFieldPosition={rteActiveFieldPosition}
              onRteSettingsCreate={handleRteSettingsCreate}
              onRteSettingsUpdate={handleRteSettingsUpdate}
              onRteSettingsMoveLeft={handleRteSettingsMoveLeft}
              onRteSettingsMoveRight={handleRteSettingsMoveRight}
              onRteSettingsDelete={handleRteSettingsDelete}
            />
          </AccordionSettings>
        </>
      ) : null}
      {!isDeleteHidden && !activeSchemaTable.root && (
      <>
        <DividerLine style={{ marginBottom: 14 }} />
        <StyledButtonsWrapper>
          <Button
            danger
            icon={ThemeIcons.settings.delete}
            text="Delete"
            onClick={handleDeleteSchemaTable}
          />
        </StyledButtonsWrapper>
      </>
      )}

    </>
  );

  return (
    <StyledWrapper>
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        key={activeSchemaId}
        settings={settingsNode}
        styles={stylesNode}
      />
    </StyledWrapper>
  );
};

/**
 * Finds the parent key whose `children` array contains the given childKey.
 *
 * @param {Object.<string, {children?: string[]}>} schemaMap
 * @param {string} childKey
 * @returns {string|null}  The parent key, or null if not found.
 */
function findParentKey(schemaMap, childKey) {
  for (const [parentKey, schema] of Object.entries(schemaMap)) {
    if (Array.isArray(schema.children) && schema.children.includes(childKey)) {
      return parentKey;
    }
  }
  return null;
}

function flattenArray ( schema, value, oldReadableId, newReadableId) {

  const flattenedArray = [];

  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);

  traverseNodes(0, value, null, rootSchemaKey);

  function traverseNodes(depth, node, parentId = null, schemaKey = null, nestedPath = '', rowIndex) {

    if (!node || typeof node !== 'object') return;

    /**
     * Handle the value (rows) array
     */
    if (Array.isArray(node)) {

      // Update nestedPath for each item (using the index for uniqueness)
      node.forEach((item, index) => {

        const itemPath = nestedPath ? `${nestedPath}.${index}` : `${index}`;
      
        traverseNodes(depth, item, parentId, schemaKey, itemPath, index);
      
      });

    }

    /**
     * Handle the row obect and row children
     */
    else {

      const { _id, cells, children } = node;

      // Create a current path for this row.
      const currentPath = nestedPath ? `${nestedPath}` : _id;


      flattenedArray.push({
        _id,
        parentId,
        schemaKey,
        schema: schema[schemaKey],
        cells: cells || {},
        rowIndex,
        depth,
        nestedPath: currentPath,

       
      });

      if (children) {

        const defaultChildSchemaKeys = schema[schemaKey]?.children || [];
        const rowChildrenSchemaKeys = children ? Object.keys(children) : [];

        const mergedSchemaKeys = defaultChildSchemaKeys.concat(rowChildrenSchemaKeys);
 
        const nestedSchemaKeys = mergedSchemaKeys.filter((item, index) => mergedSchemaKeys.indexOf(item) === index); //Remove duplicates

        nestedSchemaKeys.forEach((childSchemaKey) => {

          const childData = children?.[childSchemaKey];
          const childPath = `${currentPath}.children.${childSchemaKey}.value`; // Append the child schema key to the current path

          if (childData ) {
            traverseNodes(depth + 1, childData.value, _id, childSchemaKey, childPath, undefined, cells);
          }
        });
      }

    }
  }

  return flattenedArray;

};

function updateRowDecorators(value, schema, targetSchemaId, rowId, updateFn) {
  const rootSchemaKey = Object.keys(schema).find((key) => schema[key].root);

  function traverse(currentValue, currentSchemaKey) {
    if (!currentValue || !Array.isArray(currentValue)) return;

    if (currentSchemaKey === targetSchemaId) {
      for (const row of currentValue) {
        if (row._id === rowId) {
          const currentAll = row.decorators?.all || [];
          const isInitialized = row.decorators?.all != null;
          const result = updateFn(currentAll, isInitialized);
          if (!row.decorators) row.decorators = {};
          row.decorators.all = result;
          return;
        }
      }
    }

    for (const row of currentValue) {
      if (!row.children) continue;
      const childSchemaKeys = schema[currentSchemaKey]?.children || [];
      for (const childKey of childSchemaKeys) {
        if (row.children?.[childKey]?.value) {
          traverse(row.children[childKey].value, childKey);
        }
      }
    }
  }

  traverse(value, rootSchemaKey);
}

export default FieldSettingsCollectionSchema;
